'use client';

import Link from 'next/link';
import { useCompanyStatisticsListing } from '@/components/analytics/CompanyStatisticsTracker';
import { ProviderLogo } from '../public/ProviderLogo';
import { type HubProvider } from '@/lib/api/public/hub';
import { getTierDisplayLabel } from '@/lib/utils/tier-display';
import { getTransportModeChipStyle } from '@/lib/transport-mode-chip-style';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface HubProviderCardProps {
  provider: HubProvider;
}

// Annual volume labels
const ANNUAL_VOLUME_LABELS: Record<string, string> = {
  up_to_1k: 'Up to 1k TEU',
  from_1k_to_5k: '1k-5k TEU',
  from_5k_to_20k: '5k-20k TEU',
  from_20k_to_100k: '20k-100k TEU',
  from_100k_to_500k: '100k-500k TEU',
  over_500k: '500k+ TEU',
};

// Provider type labels
const providerTypeLabels: Record<string, string> = {
  forwarder: 'Freight Forwarder',
  nvocc: 'NVOCC',
  trucking: 'Trucking',
  carrier: 'Carrier',
};

// Transport mode labels
const transportModeLabels: Record<string, string> = {
  ocean: 'Ocean',
  road: 'Road',
  rail: 'Rail',
  air: 'Air',
};

export const HubProviderCard = ({ provider }: HubProviderCardProps) => {
  const listing = useCompanyStatisticsListing(provider.statistics, provider.statistics?.surface || 'directory');
  const trackClick = () => { listing.recordClick(); };
  const trackAuxClick = (event: React.MouseEvent) => {
    if (event.button === 1) {
      listing.recordClick();
    }
  };

  // Format location - show only country
  const locationDisplay = provider.location?.countryName || null;

  // Truncate description
  const maxDescriptionLength = 250;
  const description = provider.description;
  const shouldTruncate =
    description && description.length > maxDescriptionLength;
  const truncatedDescription = shouldTruncate
    ? description.slice(0, maxDescriptionLength).trim() + '...'
    : description;

  // Format annual volume
  const annualVolumeDisplay = provider.annualVolumeTeu
    ? ANNUAL_VOLUME_LABELS[provider.annualVolumeTeu] || provider.annualVolumeTeu
    : null;

  return (
    <TooltipProvider delayDuration={200}>
      <article ref={listing.marker} className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg hover:border-gray-300 transition-all duration-200 group">
        <div className="p-4 sm:p-6">
          {/* Top Section: Logo, Name, Provider Types | CTA Button */}
          <div className="flex items-start justify-between gap-4">
            {/* Left: Logo & Main Info */}
            <div className="flex items-center gap-4 min-w-0 flex-1">
              <div className="relative flex-shrink-0">
                <ProviderLogo
                  logoUrl={provider.logoUrl}
                  companyName={provider.companyName}
                  size="lg"
                  isLight={provider.isLight ?? false}
                />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <Link
                    href={`/providers/${provider.slug}/`}
                    onClick={trackClick}
                    onAuxClick={trackAuxClick}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block hover:underline transition-all"
                  >
                    <h3 className="text-xl font-semibold text-gray-900">
                      {provider.companyName}
                    </h3>
                  </Link>
                  {provider.claimStatus === 'claimed' && getTierDisplayLabel(provider.tier) && (
                    <span className="text-[10px] font-medium px-1.5 py-0.5 rounded text-gray-400 bg-gray-50 border border-gray-200">
                      {getTierDisplayLabel(provider.tier)}
                    </span>
                  )}
                </div>

                {/* Provider Types as inline metadata */}
                {provider.providerTypes.length > 0 && (
                  <p className="mt-1 text-sm font-medium text-slate-500">
                    {provider.providerTypes
                      .map((type) => providerTypeLabels[type] || type)
                      .join(' · ')}
                  </p>
                )}
              </div>
            </div>

            {/* Right: CTA Button */}
            <div className="flex-shrink-0">
              <Link
                href={`/providers/${provider.slug}/`}
                onClick={trackClick}
                onAuxClick={trackAuxClick}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-3 py-2 sm:px-5 sm:py-2.5 bg-gray-900 hover:bg-gray-800 text-white text-sm font-medium rounded-lg transition-colors shadow-sm hover:shadow whitespace-nowrap"
              >
                <span className="hidden sm:inline">View Profile</span>
                <span className="sm:hidden">Profile</span>
                <svg
                  className="w-4 h-4 ml-1.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </Link>
            </div>
          </div>

          {/* 3-Column Info Section */}
          <div className="grid grid-cols-2 sm:grid-cols-[1fr_1fr_3fr] gap-x-4 gap-y-3 sm:gap-6 mt-4 pt-4 border-t border-gray-100 text-sm">
            {/* Column 1: Location, Founded Year, Annual Volume */}
            <div className="flex flex-col gap-2 text-gray-600">
              {/* Location */}
              <div className={`flex items-center gap-1.5 ${locationDisplay ? 'text-gray-600' : 'text-gray-400'}`}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <svg
                      className="w-4 h-4 text-gray-400 flex-shrink-0 "
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1.5"
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1.5"
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <p>HQ Location</p>
                  </TooltipContent>
                </Tooltip>
                <span className={locationDisplay ? '' : 'italic text-sm'}>
                  {locationDisplay || 'N/A'}
                </span>
              </div>

              {/* Founded Year */}
              <div className={`flex items-center gap-1.5 ${provider.foundedYear ? 'text-gray-600' : 'text-gray-400'}`}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <svg
                      className="w-4 h-4 text-gray-400 flex-shrink-0 "
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1.5"
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <p>Founded</p>
                  </TooltipContent>
                </Tooltip>
                <span className={provider.foundedYear ? '' : 'italic text-sm'}>
                  {provider.foundedYear ? `Est. ${provider.foundedYear}` : 'N/A'}
                </span>
              </div>

              {/* Annual Volume */}
              <div className={`flex items-center gap-1.5 ${annualVolumeDisplay ? 'text-gray-600' : 'text-gray-400'}`}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <svg
                      className="w-4 h-4 flex-shrink-0 "
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1.5"
                        d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                      />
                    </svg>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <p>Annual Volume</p>
                  </TooltipContent>
                </Tooltip>
                <span className={annualVolumeDisplay ? '' : 'italic text-sm'}>
                  {annualVolumeDisplay || 'N/A'}
                </span>
              </div>
            </div>

            {/* Column 2: Transport Modes, Shipment Types */}
            <div className="flex flex-col gap-2">
              {/* Transport Modes */}
              {provider.transportModes.length > 0 && (
                <div className="flex items-center gap-1.5">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <svg
                        className="w-4 h-4 text-gray-400 flex-shrink-0 "
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="1.5"
                          d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                        />
                      </svg>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      <p>Transport Modes</p>
                    </TooltipContent>
                  </Tooltip>
                  <div className="flex flex-wrap items-center gap-1">
                    {Array.from(new Set(provider.transportModes)).map((mode) => (
                      <span
                        key={mode}
                        className="inline-flex items-center px-1.5 py-0.5 rounded text-xs border"
                        style={getTransportModeChipStyle(mode)}
                      >
                        {transportModeLabels[mode] || mode}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Shipment Types */}
              <div className={`flex items-start gap-1.5 ${provider.shipmentTypes?.length > 0 ? 'text-gray-600' : 'text-gray-400'}`}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <svg
                      className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1.5"
                        d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
                      />
                    </svg>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <p>Shipment Types</p>
                  </TooltipContent>
                </Tooltip>
                {provider.shipmentTypes && provider.shipmentTypes.length > 0 ? (
                  <div className="flex flex-wrap items-center gap-1">
                    {provider.shipmentTypes.map((st) => (
                      <span
                        key={st.code}
                        className="inline-flex items-center px-1.5 py-0.5 rounded bg-gray-50 text-xs text-gray-700"
                      >
                        {st.code.toUpperCase()}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="italic text-sm">N/A</span>
                )}
              </div>
            </div>

            {/* Column 3: Tagline & Description */}
            <div className="col-span-2 sm:col-span-1 border-t sm:border-t-0 border-gray-100 pt-3 sm:pt-0 text-gray-600">
              {provider.tagline && (
                <p className="text-sm font-semibold text-gray-800 mb-1">{provider.tagline}</p>
              )}
              {truncatedDescription ? (
                <p className={`text-sm leading-relaxed ${provider.tagline ? 'line-clamp-2' : 'line-clamp-3'}`}>
                  {truncatedDescription}
                  {shouldTruncate && (
                    <>
                      {' '}
                      <Link
                        href={`/providers/${provider.slug}/`}
                        onClick={trackClick}
                        onAuxClick={trackAuxClick}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[var(--ra-green)] hover:underline font-medium inline-flex items-center gap-0.5"
                      >
                        <span>Read more</span>
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                          />
                        </svg>
                      </Link>
                    </>
                  )}
                </p>
              ) : (
                <p className="text-sm text-gray-400 italic">No description available</p>
              )}
            </div>
          </div>
        </div>
      </article>
    </TooltipProvider>
  );
};
